import apiClient from '../client';
import type { PaginatedResponse } from '../../types';

export interface DisbursementItem {
  source: 'PAYMENT_REQUEST' | 'EXPENSE';
  id: string;
  requestNumber: string;
  paymentId?: string;
  type: string; // EXPENSE | CONTRACTOR | PROCUREMENT | OTHER
  description: string;
  categoryName?: string;
  contractorName?: string;
  projectId?: string;
  projectName?: string;
  farmOperationId?: string;
  farmOperationName?: string;
  operationTaskId?: string;
  operationTaskTitle?: string;
  amount: number;
  status: string;
  requestedById?: string;
  requestedByName?: string;
  approvalDate?: string;
}

export interface DisbursementSummary {
  awaitingTotal: number;
  disbursedTotal: number;
  pendingCount: number;
}

interface DisbursementListParams {
  page?: number;
  size?: number;
  search?: string;
  projectId?: string;
  farmOperationId?: string;
  operationTaskId?: string;
}

const buildQuery = (params: DisbursementListParams) => {
  const { page = 0, size = 10, search, projectId, farmOperationId, operationTaskId } = params;
  const qs = new URLSearchParams({ page: page.toString(), size: size.toString() });
  if (search) qs.set('search', search);
  if (projectId) qs.set('projectId', projectId);
  if (farmOperationId) qs.set('farmOperationId', farmOperationId);
  if (operationTaskId) qs.set('operationTaskId', operationTaskId);
  return qs;
};

const disbursementService = {
  getPending: async (params: DisbursementListParams = {}) => {
    const res = await apiClient.get<PaginatedResponse<DisbursementItem>>(`/disbursements/pending?${buildQuery(params)}`);
    return res.data;
  },

  getHistory: async (params: DisbursementListParams = {}) => {
    const res = await apiClient.get<PaginatedResponse<DisbursementItem>>(`/disbursements/history?${buildQuery(params)}`);
    return res.data;
  },

  getSummary: async (params: Omit<DisbursementListParams, 'page' | 'size'> = {}) => {
    const { search, projectId, farmOperationId, operationTaskId } = params;
    const qs = new URLSearchParams();
    if (search) qs.set('search', search);
    if (projectId) qs.set('projectId', projectId);
    if (farmOperationId) qs.set('farmOperationId', farmOperationId);
    if (operationTaskId) qs.set('operationTaskId', operationTaskId);
    const suffix = qs.toString() ? `?${qs.toString()}` : '';
    const res = await apiClient.get<DisbursementSummary>(`/disbursements/summary${suffix}`);
    return res.data;
  },
};

export default disbursementService;
